
import { defineComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { BaseButton, BaseIcon, CircleLoader } from 'coghent-vue-3-component-library'
import Dropzone from 'dropzone'
import StoreFactory from '@/stores/StoreFactory'
import { ConfigStore } from '@/stores/ConfigStore'
import { uploadState, useUpload } from 'coghent-vue-3-component-library'
import { upload_max_size_exceeded, upload_unsupported_file_extension, upload_duplicate_detected } from '@/composables/useNotifications'
import { apolloClient } from '@/app'

export default defineComponent({
  name: 'UploadStepOne',
  components: {
    BaseButton,
    BaseIcon,
    CircleLoader,
  },
  emits: ['stepDone'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const dropzoneContainer = ref<HTMLElement | null>(null)
    const dropzonePreviewContainer = ref<HTMLElement | null>(null)
    const addedFiles = ref<number>(0)
    const filesUploaded = ref<boolean>(false)
    const configStore = StoreFactory.get(ConfigStore)
    const dropzone = ref<Dropzone | null>(null)
    const MAX_FILES = ref<number>(1)
    const MAX_FILE_SIZE = ref<number>(200000000)
    const ACCEPTED_FILE_EXTENSIONS = ref<string>(`.png, .tif, .jpg, .jpeg`)
    const isLoading = ref<boolean>(true)

    const { setBase64Image, setFile, detectDuplicate } = useUpload()

    const createDropzone = () => {
      if (dropzoneContainer.value != null) {
        dropzone.value === null
          ? (dropzone.value = new Dropzone(dropzoneContainer.value, {
              url: 'upload/dropzone',
              autoProcessQueue: true,
              acceptedFiles: ACCEPTED_FILE_EXTENSIONS.value,
              previewTemplate: dropzonePreviewContainer.value?.outerHTML,
              uploadMultiple: false,
              parallelUploads: 2,
              maxFiles: MAX_FILES.value,
              maxFilesize: MAX_FILE_SIZE.value,
            }))
          : null
        isLoading.value = false
        dropzone.value.on(`addedfile`, (file) => {
          if (file.size > MAX_FILE_SIZE.value || file.accepted === false) {
            file.size > MAX_FILE_SIZE.value ? upload_max_size_exceeded(MAX_FILE_SIZE.value, file.size) : null
            dropzone.value!.removeFile(file)
          }
        })
        dropzone.value.on(`removedfile`, (val) => {
          addedFiles.value = dropzone.value!.files.length
        })
        dropzone.value.on(`processing`, (val) => {
          isLoading.value = true
        })
        dropzone.value.on(`complete`, (val) => {
          for (const file of dropzone.value!.files) {
            file.accepted === false ? dropzone.value!.removeFile(file) : null
            file.accepted === false ? upload_unsupported_file_extension(ACCEPTED_FILE_EXTENSIONS.value) : null
          }

          const reader = new FileReader()
          reader.onloadend = function () {
            detectDuplicate(apolloClient, reader.result).catch((err: Error) => {
              if (err.message.includes('409')) {
                for (const file of dropzone.value!.files) {
                  dropzone.value!.removeFile(file)
                }
                upload_duplicate_detected()
              }
            })
          }
          reader.readAsBinaryString(val)

          addedFiles.value = dropzone.value!.files.length
          addedFiles.value === MAX_FILES.value ? (filesUploaded.value = true) : null
          isLoading.value = false
        })
      }
    }

    watch(addedFiles, (files) => {
      if (files === MAX_FILES.value) {
        dropzone.value?.disable()
        setFile(dropzone.value!.files[0])
        setBase64Image(uploadState.file.dataURL ? uploadState.file.dataURL : null)
        emit(`stepDone`, files === MAX_FILES.value)
      } else {
        dropzone.value?.enable()
        emit(`stepDone`, files === MAX_FILES.value)
      }
    })

    const openFileExplorer = (event: any) => {
      if (dropzoneContainer.value != null && addedFiles.value < MAX_FILES.value) {
        dropzoneContainer.value.click()
      }
    }

    const init = () => {
      configStore.config.value.upload.maxFileSizeBytes ? (MAX_FILE_SIZE.value = configStore.config.value.upload.maxFileSizeBytes) : null
      configStore.config.value.upload.acceptedUploadFiles ? (ACCEPTED_FILE_EXTENSIONS.value = configStore.config.value.upload.acceptedUploadFiles) : null
      configStore.config.value.upload.maxFiles ? (MAX_FILES.value = configStore.config.value.upload.maxFiles) : null
      createDropzone()
    }

    onMounted(() => {
      init()
    })

    init()

    return {
      t,
      dropzoneContainer,
      dropzonePreviewContainer,
      addedFiles,
      openFileExplorer,
      filesUploaded,
      isLoading,
    }
  },
})
